import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Neutral is position 0; expressions are 1..n. activeIndex is -1 for neutral.
export const stepExpression = (activeIndex, dir, count) => {
  const pos = (activeIndex + 1 + dir + count + 1) % (count + 1);
  return pos - 1;
};

export const exprLabel = (list, activeIndex) => (activeIndex < 0
  ? 'NEUTRAL'
  : `EXPR ${String(activeIndex + 1).padStart(2, '0')} / ${String(list.length).padStart(2, '0')} · ${list[activeIndex].label.toUpperCase()}`);

export default function ExpressionStrip({ list, activeIndex, onSelect, showNav = true }) {
  const stripRef = useRef(null);
  const touchX = useRef(null);
  const step = (dir) => onSelect(stepExpression(activeIndex, dir, list.length));

  useEffect(() => {
    const el = stripRef.current?.querySelector('[aria-pressed="true"]');
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [activeIndex]);

  return (
    <div className="shrink-0 mt-3 space-y-2" data-testid="expression-panel">
      {showNav && <div className="flex items-center gap-2 select-none"
           onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
           onTouchEnd={(e) => {
             if (touchX.current == null) return;
             const dx = e.changedTouches[0].clientX - touchX.current;
             touchX.current = null;
             if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
           }}>
        <StepBtn onClick={() => step(-1)} label="Previous expression" testId="expr-prev"><ChevronLeft className="w-5 h-5" /></StepBtn>
        <div className="flex-1 min-w-0 h-10 flex items-center justify-center border border-[#3a2512] rounded-md bg-[#0a0502] font-osd text-xs sm:text-sm tracking-widest text-[#9dff7a] truncate px-2"
             style={{ textShadow: '0 0 6px rgba(140,255,110,0.7)' }} aria-live="polite" data-testid="expr-current">
          {exprLabel(list, activeIndex)}
        </div>
        <StepBtn onClick={() => step(1)} label="Next expression" testId="expr-next"><ChevronRight className="w-5 h-5" /></StepBtn>
      </div>}
      <div ref={stripRef} role="toolbar" aria-label="Facial expressions"
           className="flex gap-2 overflow-x-auto no-scrollbar snap-x pb-1 sm:flex-wrap sm:overflow-x-visible sm:max-h-[96px] sm:overflow-y-auto">
        <Chip active={activeIndex < 0} onClick={() => onSelect(-1)} testId="expr-chip-neutral">NEUTRAL</Chip>
        {list.map((e, i) => (
          <Chip key={e.key} active={activeIndex === i} onClick={() => onSelect(i)} testId={`expr-chip-${i}`}>
            <span className="opacity-70 mr-1.5">{String(i + 1).padStart(2, '0')}</span>{e.label.toUpperCase()}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} data-testid={testId}
            className={`header-tab snap-start shrink-0 h-10 px-3 font-osd text-xs tracking-wider border-2 rounded-sm whitespace-nowrap ${active ? 'header-tab-active' : ''}`}>
      {children}
    </button>
  );
}

function StepBtn({ onClick, label, testId, children }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label} data-testid={testId}
            className="header-tab shrink-0 w-11 h-10 border-2 rounded-sm flex items-center justify-center">
      {children}
    </button>
  );
}
